import React, { useState } from 'react';
import { Web3State } from '../hooks/useWeb3';

interface HeaderProps {
  web3: Web3State & {
    connect: () => void;
    disconnect: () => void;
    switchToBSC: () => void;
  };
  currentPage: string;
  onNavigate: (page: string) => void;
}

function shortAddress(addr: string) {
  return addr.slice(0, 6) + '...' + addr.slice(-4);
}

// ---- Professional SVG Icons ----
const IconMarket = ({ active }: { active: boolean }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? '#00d4ff' : '#6b7d99'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8M12 17v4" />
    <path d="M7 8l3 3 3-3 4 4" />
  </svg>
);

const IconAdd = ({ active }: { active: boolean }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? '#00d4ff' : '#6b7d99'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v8M8 12h8" />
  </svg>
);

const IconPortfolio = ({ active }: { active: boolean }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? '#00d4ff' : '#6b7d99'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="7.5 4.21 12 6.81 16.5 4.21" />
    <polyline points="7.5 19.79 7.5 14.6 3 12" />
    <polyline points="21 12 16.5 14.6 16.5 19.79" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

const IconAnnounce = ({ active }: { active: boolean }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? '#00d4ff' : '#6b7d99'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
  </svg>
);

const NAV_ITEMS = [
  { key: 'home',         label: '市场',  Icon: IconMarket },
  { key: 'announce',     label: '预告',  Icon: IconAnnounce },
  { key: 'add',          label: '上币',  Icon: IconAdd },
  { key: 'portfolio',    label: '持仓',  Icon: IconPortfolio },
];

const Header: React.FC<HeaderProps> = ({ web3, currentPage, onNavigate }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      {/* Top Header */}
      <header style={styles.header}>
        <div style={styles.container}>
          {/* Logo */}
          <div style={styles.logo} onClick={() => onNavigate('home')}>
            <svg width="26" height="26" viewBox="0 0 28 28" fill="none">
              <defs>
                <linearGradient id="lg" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#00d4ff" />
                  <stop offset="100%" stopColor="#7c4dff" />
                </linearGradient>
              </defs>
              <path d="M14 2C7.37 2 2 7.37 2 14s5.37 12 12 12 12-5.37 12-12S20.63 2 14 2z" fill="url(#lg)" opacity="0.2" />
              <path d="M8 14 Q14 6 20 14 Q14 22 8 14Z" fill="url(#lg)" />
            </svg>
            <span style={styles.logoText}>Easy</span>
            <span style={styles.logoBadge}>BSC</span>
          </div>

          {/* PC Nav */}
          <nav style={styles.pcNav} className="pc-nav">
            {NAV_ITEMS.map(item => (
              <button
                key={item.key}
                style={{ ...styles.navBtn, ...(currentPage === item.key ? styles.navBtnActive : {}) }}
                onClick={() => onNavigate(item.key)}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Wallet Area */}
          <div style={styles.walletArea}>
            {!web3.isConnected ? (
              <button style={styles.connectBtn} onClick={web3.connect} disabled={web3.isConnecting}>
                {web3.isConnecting ? '连接中...' : '连接钱包'}
              </button>
            ) : (
              <div style={styles.walletInfo}>
                {!web3.isBSC && (
                  <button style={styles.switchBtn} onClick={web3.switchToBSC}>⚠ 切换BSC</button>
                )}
                <div style={styles.accountBox} onClick={() => setMenuOpen(v => !v)}>
                  <span style={web3.isBSC ? styles.dotGreen : styles.dotRed} />
                  <span style={styles.accountAddr}>{shortAddress(web3.account!)}</span>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ marginLeft: 2 }}>
                    <path d="M2 4l4 4 4-4" stroke="#6b7d99" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                {menuOpen && (
                  <div style={styles.dropdown}>
                    <div style={styles.dropdownItem}>
                      <span style={styles.dropdownLabel}>余额</span>
                      <span style={styles.dropdownValue}>{web3.balance} BNB</span>
                    </div>
                    <div style={styles.dropdownItem}>
                      <span style={styles.dropdownLabel}>网络</span>
                      <span style={{ ...styles.dropdownValue, color: web3.isBSC ? '#00e676' : '#ff4d6d' }}>
                        {web3.isBSC ? 'BSC 主网' : '非BSC网络'}
                      </span>
                    </div>
                    <button style={styles.disconnectBtn} onClick={() => { web3.disconnect(); setMenuOpen(false); }}>
                      断开连接
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Ad Banner — only on market home */}
      {currentPage === 'home' && (
        <a
          href="https://dexplus.fun"
          target="_blank"
          rel="noopener noreferrer"
          style={styles.adBanner}
        >
          <span style={styles.adTag}>AD</span>
          <span style={styles.adText}>
            🚀 0代码部署智能合约 · 点击此处立即前往
          </span>
          <span style={styles.adArrow}>→</span>
        </a>
      )}

      {/* Mobile Bottom Navigation — only 3 items, no wallet button */}
      <nav className="mobile-bottom-nav">
        {NAV_ITEMS.map(item => {
          const active = currentPage === item.key;
          return (
            <button
              key={item.key}
              className={`bottom-nav-item${active ? ' bottom-nav-active' : ''}`}
              onClick={() => onNavigate(item.key)}
            >
              <item.Icon active={active} />
              <span className={`bottom-nav-label${active ? ' bottom-nav-label-active' : ''}`}>
                {item.label}
              </span>
            </button>
          );
        })}
      </nav>
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    background: 'rgba(10,14,26,0.97)',
    backdropFilter: 'blur(20px)',
    borderBottom: '1px solid #1e2d4a',
    padding: '0 16px',
  },
  container: {
    maxWidth: 1400,
    margin: '0 auto',
    height: 58,
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    flexShrink: 0,
  },
  logoText: {
    fontSize: 20,
    fontWeight: 800,
    background: 'linear-gradient(135deg,#00d4ff,#7c4dff)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  logoBadge: {
    fontSize: 9,
    fontWeight: 700,
    background: 'rgba(0,212,255,0.15)',
    color: '#00d4ff',
    border: '1px solid rgba(0,212,255,0.3)',
    borderRadius: 4,
    padding: '2px 5px',
  },
  pcNav: {
    display: 'flex',
    gap: 2,
    flex: 1,
  },
  navBtn: {
    background: 'transparent',
    border: 'none',
    color: '#6b7d99',
    fontSize: 14,
    fontWeight: 500,
    padding: '7px 14px',
    borderRadius: 8,
    cursor: 'pointer',
  },
  navBtnActive: {
    background: 'rgba(0,212,255,0.1)',
    color: '#00d4ff',
  },
  walletArea: {
    flexShrink: 0,
    position: 'relative' as const,
  },
  connectBtn: {
    background: 'linear-gradient(135deg,#00d4ff,#7c4dff)',
    border: 'none',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    padding: '9px 18px',
    borderRadius: 10,
    cursor: 'pointer',
    boxShadow: '0 0 16px rgba(0,212,255,0.3)',
  },
  walletInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    position: 'relative' as const,
  },
  switchBtn: {
    background: 'rgba(255,214,0,0.12)',
    border: '1px solid rgba(255,214,0,0.35)',
    color: '#ffd600',
    fontSize: 11,
    fontWeight: 600,
    padding: '5px 10px',
    borderRadius: 7,
    cursor: 'pointer',
  },
  accountBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid #1e2d4a',
    borderRadius: 10,
    padding: '7px 12px',
    cursor: 'pointer',
  },
  dotGreen: {
    width: 7, height: 7, borderRadius: '50%',
    background: '#00e676', boxShadow: '0 0 5px #00e676',
    display: 'inline-block', flexShrink: 0,
  },
  dotRed: {
    width: 7, height: 7, borderRadius: '50%',
    background: '#ff4d6d', display: 'inline-block', flexShrink: 0,
  },
  accountAddr: {
    fontSize: 13,
    color: '#e8eef7',
    fontFamily: 'monospace',
  },
  dropdown: {
    position: 'absolute' as const,
    top: '110%',
    right: 0,
    background: '#131929',
    border: '1px solid #1e2d4a',
    borderRadius: 12,
    padding: '12px',
    minWidth: 180,
    zIndex: 200,
    boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
  },
  dropdownItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 4px',
  },
  dropdownLabel: { fontSize: 12, color: '#6b7d99' },
  dropdownValue: { fontSize: 13, fontWeight: 600, color: '#e8eef7' },
  disconnectBtn: {
    background: 'rgba(255,77,109,0.1)',
    border: '1px solid rgba(255,77,109,0.3)',
    color: '#ff4d6d',
    fontSize: 13,
    fontWeight: 600,
    padding: '9px',
    borderRadius: 8,
    cursor: 'pointer',
    width: '100%',
    marginTop: 4,
  },
  adBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    background: 'linear-gradient(90deg, rgba(0,212,255,0.08) 0%, rgba(124,77,255,0.08) 100%)',
    borderBottom: '1px solid rgba(0,212,255,0.12)',
    padding: '8px 16px',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  adTag: {
    fontSize: 9,
    fontWeight: 800,
    color: '#00d4ff',
    background: 'rgba(0,212,255,0.15)',
    border: '1px solid rgba(0,212,255,0.3)',
    borderRadius: 4,
    padding: '2px 5px',
    letterSpacing: '0.5px',
    flexShrink: 0,
  },
  adText: {
    fontSize: 13,
    color: '#a0b0c8',
  },
  adArrow: {
    fontSize: 14,
    color: '#00d4ff',
    flexShrink: 0,
  },
};

export default Header;
